import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { KPICard } from "@/components/collections/kpi-card";
import {
  Search,
  DollarSign,
  Receipt as ReceiptIcon,
  Target,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  AlertCircle,
  CheckCircle2,
  X,
  ArrowRightCircle,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useReceiptsPageData } from "@/hooks/useReceiptsData";
import type { AllocationStatus } from "@/types/payments";

type SortField = "receiptNumber" | "customerName" | "receiptDate" | "receivedAmount" | "unappliedAmount" | "allocationStatus";
type SortDirection = "asc" | "desc" | null;

export default function ReceiptsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [promiseFilter, setPromiseFilter] = useState("all");

  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Get data from hook
  const { receipts, isLoading } = useReceiptsPageData();

  // Calculate KPIs from receipts
  const kpis = useMemo(() => {
    const totalReceipts = receipts.length;
    const totalReceived = receipts.reduce((sum, r) => sum + r.receivedAmount, 0);
    
    const fullyAllocated = receipts.filter(r => r.allocationStatus === 'fully_allocated');
    const partiallyAllocated = receipts.filter(r => r.allocationStatus === 'partially_allocated');
    const unallocated = receipts.filter(r => r.allocationStatus === 'unallocated');
    
    return {
      totalReceipts,
      totalReceived,
      fullyApplied: fullyAllocated.reduce((sum, r) => sum + r.receivedAmount, 0),
      fullyAppliedCount: fullyAllocated.length,
      partiallyApplied: partiallyAllocated.reduce((sum, r) => sum + r.receivedAmount, 0),
      partiallyAppliedCount: partiallyAllocated.length,
      unapplied: unallocated.reduce((sum, r) => sum + r.receivedAmount, 0),
      unappliedCount: unallocated.length,
    };
  }, [receipts]);

  // Filter receipts based on search, status, and KPI filters
  const filteredReceipts = useMemo(() => {
    let filtered = receipts;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (receipt) =>
          receipt.receiptNumber.toLowerCase().includes(query) ||
          receipt.customerName.toLowerCase().includes(query) ||
          receipt.customerAccountNumber.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((receipt) => receipt.allocationStatus === statusFilter);
    }

    // Apply promise filter
    if (promiseFilter !== "all") {
      if (promiseFilter === "promise") {
        filtered = filtered.filter((receipt) => receipt.hasPromise);
      } else if (promiseFilter === "non-promise") {
        filtered = filtered.filter((receipt) => !receipt.hasPromise);
      }
    }

    return filtered;
  }, [receipts, searchQuery, statusFilter, promiseFilter]);

  // Sort receipts
  const sortedReceipts = useMemo(() => {
    if (!sortField || !sortDirection) return filteredReceipts;

    return [...filteredReceipts].sort((a, b) => {
      let aValue: string | number | Date = a[sortField];
      let bValue: string | number | Date = b[sortField];

      // Handle date sorting
      if (sortField === "receiptDate") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Handle numeric sorting
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortDirection === "asc") {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });
  }, [filteredReceipts, sortField, sortDirection]);

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Render sort icon
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    return <ArrowDown className="ml-2 h-4 w-4" />;
  };

  const getStatusBadge = (status: AllocationStatus) => {
    switch (status) {
      case "fully_allocated":
        return (
          <Badge variant="outline" className="border-green-500 text-green-500">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Fully Allocated
          </Badge>
        );
      case "partially_allocated":
        return (
          <Badge variant="outline" className="border-yellow-500 text-yellow-500">
            <AlertCircle className="mr-1 h-3 w-3" />
            Partially Allocated
          </Badge>
        );
      case "unallocated":
        return (
          <Badge variant="outline" className="border-red-500 text-red-500">
            <X className="mr-1 h-3 w-3" />
            Unallocated
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  return (
    <div className="flex-1 space-y-4 p-4 md:p-6 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Receipts</h2>
          <p className="text-muted-foreground">
            View and manage customer receipts and payment allocations
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Total Receipts"
          value={formatCurrency(kpis.totalReceived)}
          subtitle={`${kpis.totalReceipts} receipts`}
          icon={DollarSign}
          trend={{ value: "+12.5%", isPositive: true }}
          colorScheme="blue"
        />
        <KPICard
          title="Fully Allocated"
          value={formatCurrency(kpis.fullyApplied)}
          subtitle={`${kpis.fullyAppliedCount} receipts`}
          icon={CheckCircle2}
          trend={{ value: "+8.2%", isPositive: true }}
          colorScheme="green"
        />
        <KPICard
          title="Partially Allocated"
          value={formatCurrency(kpis.partiallyApplied)}
          subtitle={`${kpis.partiallyAppliedCount} receipts`}
          icon={AlertCircle}
          trend={{ value: "-3.1%", isPositive: false }}
          colorScheme="amber"
        />
        <KPICard
          title="Unallocated"
          value={formatCurrency(kpis.unapplied)}
          subtitle={`${kpis.unappliedCount} receipts`}
          icon={Target}
          trend={{ value: "-5.4%", isPositive: false }}
          colorScheme="red"
        />
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by receipt number, customer..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="fully_allocated">Fully Allocated</SelectItem>
                  <SelectItem value="partially_allocated">Partially Allocated</SelectItem>
                  <SelectItem value="unallocated">Unallocated</SelectItem>
                </SelectContent>
              </Select>
              <Select value={promiseFilter} onValueChange={setPromiseFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Promise Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Receipts</SelectItem>
                  <SelectItem value="promise">Promise to Pay</SelectItem>
                  <SelectItem value="non-promise">Non-Promise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("receiptNumber")}
                  >
                    <div className="flex items-center">
                      Receipt #
                      {renderSortIcon("receiptNumber")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("customerName")}
                  >
                    <div className="flex items-center">
                      Customer
                      {renderSortIcon("customerName")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("receiptDate")}
                  >
                    <div className="flex items-center">
                      Receipt Date
                      {renderSortIcon("receiptDate")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-right"
                    onClick={() => handleSort("receivedAmount")}
                  >
                    <div className="flex items-center justify-end">
                      Received Amount
                      {renderSortIcon("receivedAmount")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-right"
                    onClick={() => handleSort("unappliedAmount")}
                  >
                    <div className="flex items-center justify-end">
                      Unallocated Amount
                      {renderSortIcon("unappliedAmount")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("allocationStatus")}
                  >
                    <div className="flex items-center">
                      Status
                      {renderSortIcon("allocationStatus")}
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading receipts...
                    </TableCell>
                  </TableRow>
                ) : sortedReceipts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      No receipts found
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedReceipts.map((receipt) => (
                    <TableRow
                      key={receipt.id}
                      className="hover:bg-muted/50"
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {receipt.receiptNumber}
                          {receipt.hasPromise && (
                            <Badge variant="outline" className="text-xs border-blue-500 bg-blue-50 text-blue-700">
                              P2P
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{receipt.customerName}</div>
                          <div className="text-sm text-muted-foreground">
                            {receipt.customerAccountNumber}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{format(new Date(receipt.receiptDate), "MMM dd, yyyy")}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(receipt.receivedAmount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={cn(
                            "font-medium",
                            receipt.unappliedAmount > 0 ? "text-red-500" : "text-muted-foreground"
                          )}
                        >
                          {formatCurrency(receipt.unappliedAmount)}
                        </span>
                      </TableCell>
                      <TableCell>{getStatusBadge(receipt.allocationStatus)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/payments/allocate/${receipt.id}`);
                          }}
                          className="gap-1"
                        >
                          <ArrowRightCircle className="h-4 w-4" />
                          Allocate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
